import { type GalleryMediaFile, useMediaFileCharacters, useMediaFileMetadata, useTranslation } from "@next-platform/client-common-core";
import getMediaFileRelatedEntity from "@next-platform/client-common-core/viewMedia/common/getMediaFileRelatedEntity";
import type { MediaFilePreviewContent, MediaFilePreviewLabels } from "@next-platform/client-common-spicy-ui";
import { MediaFileConnectionType } from "@next-platform/packages-ts-types";
import { useCallback, useMemo } from "react";

import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils/getAvatarUrl";

import type { MediaFileLinkTarget } from "./getMediaFileConnectionPath";
import mediaFileKindIcons from "./mediaFileKindIcons";

type MediaFilePreviewContentOptions = {
  readonly file: GalleryMediaFile;
  readonly isBlurred?: boolean;
  readonly onOpen?: (file: GalleryMediaFile) => void;
};

export default function useMediaFilePreviewContent({ file, isBlurred, onOpen }: MediaFilePreviewContentOptions) {
  const { t } = useTranslation();
  const metadata = useMediaFileMetadata(file);

  // The more-menu labels reuse the details view's own keys (mediaFile:details.*) rather than minting Gallery-
  // specific duplicates: it is the same seven actions, just anchored to a tile instead of the details sidebar.
  const labels = useMemo<MediaFilePreviewLabels>(
    () => ({
      imageAlt: t("gallery:mediaFilePreview.imageAlt"),
      open: t("gallery:mediaFilePreview.open"),
      mature: t("gallery:mediaFilePreview.mature"),
      openCharacter: t("mediaFile:details.openCharacterAriaLabel"),
      noRelationLabel: t("gallery:mediaFilePreview.noRelation", { defaultValue: "No relation" }),
      copyLink: t("gallery:mediaFilePreview.copyLink"),
      moreAriaLabel: t("mediaFile:details.moreAriaLabel"),
      downloadAriaLabel: t("mediaFile:details.downloadAriaLabel"),
      openOriginalLabel: t("mediaFile:details.openOriginal"),
      keepAriaLabel: t("mediaFile:details.keepAriaLabel"),
      unkeepLabel: t("mediaFile:details.unkeepLabel", { defaultValue: "Un-keep" }),
      makePrivateLabel: t("mediaFile:details.makePrivateLabel", { defaultValue: "Make private" }),
      addToFolderLabel: t("mediaFile:details.addToFolderLabel", { defaultValue: "Add to folder" }),
      reportLabel: t("mediaFile:details.reportLabel", { defaultValue: "Report" }),
      deleteLabel: t("mediaFile:details.deleteLabel", { defaultValue: "Delete" }),
    }),
    [t],
  );

  const { primaryCharacter, connections } = useMediaFileCharacters({ file });
  const relatedEntity = getMediaFileRelatedEntity({ kind: metadata.kind, primaryCharacter, connections });
  // A response that carried a chatbot id but no resolved chatbot still links to it, under the generic word.
  const linkTarget = useMemo<MediaFileLinkTarget | undefined>(
    () => relatedEntity ?? (metadata.characterId ? { type: MediaFileConnectionType.CHARACTER, id: metadata.characterId } : undefined),
    [relatedEntity, metadata.characterId],
  );

  // The embed carries the stored key, not a resolved URL, so it goes through the same helper the details view
  // uses rather than straight to an <img>.
  const relatedEntityAvatarUrl = relatedEntity?.imageUrl
    ? (getAvatarUrl(relatedEntity.imageUrl, { preset: AvatarPreset.AVATAR_48X48 }) ?? undefined)
    : undefined;

  const content = useMemo<MediaFilePreviewContent>(
    () => ({
      imageUrl: metadata.imageUrl,
      width: metadata.width,
      height: metadata.height,
      // Both the badge and the blur wait for a verdict: an unjudged file reads `is_nsfw: false` because
      // nobody looked, so marking or hiding it on that alone would state something the data does not say.
      isNsfw: metadata.isNsfw && metadata.isModerated,
      isBlurred: metadata.isNsfw && metadata.isModerated && isBlurred !== false,
      isKept: file.is_kept_by_me,
      kindIcon: mediaFileKindIcons[metadata.kind],
      kindLabel: metadata.kindLabel,
      isUnattached: metadata.isUnattached,
      // The entity's own name, with the generic word only as a fallback for a response that carried a chatbot
      // id but no resolved chatbot.
      ...(linkTarget
        ? {
            characterLabel: relatedEntity?.name ?? t("gallery:mediaFilePreview.character"),
            ...(relatedEntityAvatarUrl ? { characterAvatarUrl: relatedEntityAvatarUrl } : {}),
          }
        : {}),
    }),
    [metadata, linkTarget, relatedEntity?.name, relatedEntityAvatarUrl, isBlurred, file.is_kept_by_me, t],
  );

  const handleOpen = useCallback(() => onOpen?.(file), [onOpen, file]);

  return { content, labels, metadata, linkTarget, handleOpen: onOpen ? handleOpen : undefined };
}
